import { DataTable, FormGroupHeader } from "@/components/shared";
import { PurchaseDetails, TenderPermissionEnum } from "@/graphql/__generated__/graphql";
import { getColumnsPurchases } from "./columns";
import { useIntl } from "react-intl";
import { useParams, useOutletContext } from "react-router-dom";
import { useRemovePurchaseMutationHook } from "@/hooks";
import { ConfirmRemoveAlert } from "../Dialog";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import { useState } from "react";
import { EditTenderContext } from "@/layouts/EditTenderLayout";
import { useTranslation } from "react-i18next";
import AddPurchase from "../AddPurchase/Form";
import EditPurchaseDialog from "./EditPurchaseDialog";
import { useFetchPurchasesOnTenderQueryHook, useFetchServicesOnTenderQueryHook } from "@/hooks/queries";

interface PurchasesTableProps {
  /** Whether to show with header (for standalone page) or inline (for embedding in Services) */
  showHeader?: boolean;
  /** Custom title for the section */
  title?: string;
  /** Custom description for the section */
  description?: string;
}

/**
 * PurchasesTable component for displaying and managing purchases in a tender.
 * Provides a table view with the ability to add, edit, and remove purchases.
 *
 * @component
 * @returns {JSX.Element} The rendered PurchasesTable component
 */
const PurchasesTable = ({ showHeader = true, title, description }: PurchasesTableProps) => {
  // Hooks
  const intl = useIntl();
  const { tenderID } = useParams<{ tenderID: string }>();
  const { tenderByIdData } = useOutletContext<EditTenderContext>();
  const { t } = useTranslation(["translation", "common", "tables", "components"]);

  // Mutation hooks
  const { removePurchaseMutation, loading: removePurchaseLoading } = useRemovePurchaseMutationHook();

  // Query hooks
  const { data: purchasesData, loading: purchasesLoading } = useFetchPurchasesOnTenderQueryHook(Number(tenderID));
  const { data: servicesData } = useFetchServicesOnTenderQueryHook(Number(tenderID), false, true);

  // States
  const [currentPurchaseDetails, setCurrentPurchaseDetails] = useState<PurchaseDetails | null>(null);
  const [showConfirmRemoveAlert, setShowConfirmRemoveAlert] = useState<boolean>(false);
  const [showAddPurchaseModal, setShowAddPurchaseModal] = useState<boolean>(false);
  const [showEditPurchaseModal, setShowEditPurchaseModal] = useState<boolean>(false);

  // Constants
  const canAddPurchase = tenderByIdData?.fetchTenderById?.permissions.includes(TenderPermissionEnum.AddService);
  const purchaseDetails = (purchasesData?.fetchPurchasesOnTender ?? []) as PurchaseDetails[];
  const services = servicesData?.fetchServicesOnTender?.services ?? [];

  /**
   * Handler for clicking the edit button on a purchase row
   * @param {PurchaseDetails} purchaseDetails - The purchase details to be edited
   */
  const handleOnClickEdit = (purchaseDetails: PurchaseDetails) => {
    setCurrentPurchaseDetails(purchaseDetails);
    setShowEditPurchaseModal(true);
  };

  /**
   * Handler for clicking the remove button on a purchase row
   * @param {PurchaseDetails} purchaseDetails - The purchase details to be removed
   */
  const handleOnClickRemove = (purchaseDetails: PurchaseDetails) => {
    setCurrentPurchaseDetails(purchaseDetails);
    setShowConfirmRemoveAlert(true);
  };

  /**
   * Handler for removing a purchase
   */
  const handleRemovePurchase = () => {
    if (!currentPurchaseDetails) return;

    removePurchaseMutation({
      variables: {
        tenderId: Number(tenderID),
        purchaseId: currentPurchaseDetails.purchase.id,
      },
    });
    setShowConfirmRemoveAlert(false);
    setCurrentPurchaseDetails(null);
  };

  // Action buttons configuration
  const actionButtons: ActionButtonConfig[] = [
    canAddPurchase
      ? {
        text: t("edittender.purchases.addpurchase"),
        actionFn: () => setShowAddPurchaseModal(true),
        variant: "default" as const,
      }
      : null,
  ].filter(Boolean) as ActionButtonConfig[];

  return (
    <>
      {showHeader && (
        <FormGroupHeader
          actionButtons={actionButtons}
          title={title || t("edittender.purchases.title")}
          description={description || t("edittender.purchases.description")}
          className="pr-4 pl-2"
        />
      )}

      {/* Purchases table */}
      <div className={showHeader ? "pr-4 pl-2 pb-[300px]" : ""}>
        <DataTable
          disableRowColour={true}
          loading={purchasesLoading}
          displayAmount={purchaseDetails.length !== 0 ? purchaseDetails.length : 5}
          data={purchaseDetails}
          columns={getColumnsPurchases(intl, t, !!canAddPurchase, handleOnClickEdit, handleOnClickRemove, removePurchaseLoading, services)}
          className="max-h-[60vh]"
          enableColumnPinning={true}
          columnPinning={{ left: [], right: ["actions"] }}
        />
      </div>

      {/* Confirm Remove Purchase Alert */}
      <ConfirmRemoveAlert
        open={showConfirmRemoveAlert}
        onOpenChange={setShowConfirmRemoveAlert}
        actionButtonFn={handleRemovePurchase}
        bodyText={t("components:alertdialogs.removepurchase")}
      />

      {/* Add Purchase Modal */}
      <AddPurchase open={showAddPurchaseModal} onOpenChange={setShowAddPurchaseModal} />

      {/* Edit Purchase Modal - Full edit with delete-create pattern */}
      <EditPurchaseDialog
        open={showEditPurchaseModal}
        onOpenChange={setShowEditPurchaseModal}
        purchaseDetails={currentPurchaseDetails}
      />
    </>
  );
};

export default PurchasesTable;
